/**
 * Scale a PDF page so its CSS width matches the available container width.
 *
 * @param {number} pageWidth
 * @param {number} containerWidth
 * @returns {number}
 */
export function getPdfFitWidthScale(pageWidth, containerWidth) {
  if (!Number.isFinite(pageWidth) || pageWidth <= 0) {
    return 1;
  }
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
    return 1;
  }
  return containerWidth / pageWidth;
}

/**
 * Cap device pixel ratio so iPad canvases stay sharp without exhausting memory.
 *
 * @param {number} devicePixelRatio
 * @returns {number}
 */
export function getPdfRenderPixelRatio(devicePixelRatio) {
  if (!Number.isFinite(devicePixelRatio) || devicePixelRatio <= 0) {
    return 1;
  }
  return Math.min(devicePixelRatio, 2);
}

export const PDF_PAGE_HORIZONTAL_PADDING = 24;
export const MIN_PDF_PAGE_WIDTH = 280;
export const MIN_READABLE_PDF_PAGE_WIDTH = 720;
export const PDF_ZOOM_MIN = 0.5;
export const PDF_ZOOM_MAX = 2.5;
export const PDF_ZOOM_STEP = 0.25;
export const PDF_ZOOM_DEFAULT = 1;

/**
 * @param {number} zoom
 * @returns {number}
 */
export function clampPdfZoom(zoom) {
  if (!Number.isFinite(zoom)) {
    return PDF_ZOOM_DEFAULT;
  }
  const clamped = Math.min(PDF_ZOOM_MAX, Math.max(PDF_ZOOM_MIN, zoom));
  return Math.round(clamped * 100) / 100;
}

/**
 * @param {number} zoom
 * @param {number} direction
 * @returns {number}
 */
export function stepPdfZoom(zoom, direction) {
  const delta = Number.isFinite(direction) ? direction : 0;
  return clampPdfZoom(clampPdfZoom(zoom) + delta * PDF_ZOOM_STEP);
}

/**
 * @param {number} containerWidth
 * @returns {number}
 */
export function getPdfPageContentWidth(containerWidth) {
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
    return MIN_PDF_PAGE_WIDTH;
  }
  return Math.max(containerWidth - PDF_PAGE_HORIZONTAL_PADDING, MIN_PDF_PAGE_WIDTH);
}

/**
 * Fit-width on large screens; keep a readable minimum width on narrow viewports.
 *
 * @param {number} pageWidth
 * @param {number} containerWidth
 * @param {number} [zoom]
 * @returns {number}
 */
export function getPdfDisplayScale(pageWidth, containerWidth, zoom = PDF_ZOOM_DEFAULT) {
  const fit = getPdfFitWidthScale(pageWidth, getPdfPageContentWidth(containerWidth));
  const readable = getPdfFitWidthScale(pageWidth, MIN_READABLE_PDF_PAGE_WIDTH);
  return Math.max(fit, readable) * clampPdfZoom(zoom);
}
